import { takeLatest } from 'redux-saga';
import { put, call, fork, select } from 'redux-saga/effects';
import { fromJS } from 'immutable';

import { updateDeliverableData, getDeliverable } from '../../utils/jsonapi-functions/deliverables';
import { addDiscussionDocument } from '../../utils/jsonapi-functions/discussions';
import { createDocument } from '../../utils/jsonapi-functions/documents';
import { createComment } from '../../utils/jsonapi-functions/comments';
import { getDiscussionId, getDiscussionName, getDeliverableObject } from '../../selectors/discussion-selectors';
import { getCampaignId, getPermissionId } from '../../selectors/campaign-selectors';
import { getUserId } from '../../selectors/user-selectors';
import { DELIVERABLE, DISCUSSION_PAGE, BLOG, VIDEO, IMAGE, TEXT } from '../../utils/static-configuration';
import { MODAL_LOADING, MODAL_ERROR, MODAL_CLEAR } from '../../constants/modal';
import { SUBMIT_DELIVERABLE, UPDATE_ADD_DELIVERABLE_FORM_OPTIONS, SET_INITIAL_SUBMIT_TYPE } from './constants';
import { SEND_MESSAGE, UPDATE_DELIVERABLES, UPDATE_DOCUMENTS } from '../../containers/discussion/constants';
import { reportError } from '../../utils/error-handling-service';
import { trackEvent } from '../../utils/metric-analytics-service';

export function* submitExistingDeliverable(action) {
  try {
    const deliverableData = action.deliverableData;
    const deliverableId = deliverableData.id;
    const version = deliverableData.version + 1;
    const submitDeliverableCallback = action.submitDeliverableCallback;
    const discussionId = yield select(getDiscussionId);
    const userId = yield select(getUserId);

    yield put({ type: MODAL_LOADING, loading: true });

    // Deliverable message submits are treated as documents
    const documentData = {
      name: deliverableData.fileType === 'upload' ? deliverableData.name : deliverableData.title,
      filePath: deliverableData.filePath,
      docType: DELIVERABLE,
      fileType: deliverableData.fileType,
      size: deliverableData.fileType === 'upload' ? deliverableData.size : 1,
      message: deliverableData.message,
      discussionId,
      approved: false,
      version,
    };

    const newDocument = yield call(createDocument, documentData);
    const newDocumentJSON = newDocument.toJSONTree();

    const commentsList = [];

    // Create version comment
    let versionComment;
    if (version > 1) {
      const commentData = {
        comment: `VERSION ${version} added`,
        commentType: 'VERSION',
        deliverableId,
        documentId: newDocumentJSON.id,
        userId,
      };
      versionComment = yield call(createComment, commentData);
      commentsList.push(versionComment);
    }

    // Update deliverable
    const insertDeliverableData = {
      document: newDocument,
      version,
    };
    if (commentsList.length > 0) insertDeliverableData.comments = commentsList;

    yield call(updateDeliverableData, deliverableId, insertDeliverableData);

    // Update deliverable doesn't return all the documents so get the deliverable data again
    // TODO Should update the update deliverable to return the entire deliverable if possible
    const updatedDeliverable = yield call(getDeliverable, deliverableId);
    const updatedDeliverableJSON = updatedDeliverable;

    // Update discussion with new document
    yield call(addDiscussionDocument, discussionId, newDocument);

    // Update document and deliverable local state
    yield put({ type: UPDATE_DOCUMENTS, documentData: fromJS(newDocumentJSON) });
    updatedDeliverableJSON.mostRecentDocument = newDocumentJSON;
    yield put({ type: UPDATE_DELIVERABLES, deliverableData: fromJS(updatedDeliverableJSON) });

    // Send new message
    const myPermissionId = yield select(getPermissionId);
    const message = updatedDeliverableJSON.title;
    const metaData = {
      messageType: 'deliverable',
      permissionId: myPermissionId,
      discussionId,
      deliverableId: updatedDeliverableJSON.id,
      docId: newDocumentJSON.id,
      deliverableStatus: 'submitted',
    };

    const campaignId = yield select(getCampaignId);
    const discussionName = yield select(getDiscussionName);

    const addDeliverableData = {
      campaignId,
      collaborationId: discussionId,
      collaborationName: discussionName,
      deliverableId: updatedDeliverableJSON.id,
      deliverableName: updatedDeliverableJSON.title,
    };
    trackEvent('Add Deliverable', addDeliverableData);

    const messageData = {
      message,
      metaData,
      page: DISCUSSION_PAGE,
    };
    yield put({ type: SEND_MESSAGE, data: messageData });

    yield call(submitDeliverableCallback);
    yield put({ type: MODAL_CLEAR });
  } catch (error) {
    reportError('ADD_DELIVERABLE_ERROR', error.message);
    yield put({ type: MODAL_ERROR, error });
  }
}

function getAvailableSubmitTypes(assetType) {
  const initialSubmitType = {
    VIDEO: 'url',
    IMAGE: 'upload',
    BLOG: 'url',
    TEXT: 'text',
  };

  const data = {
    submitType: initialSubmitType[assetType],
    isUploadActive: assetType !== TEXT,
    isUrlActive: assetType === VIDEO || assetType === BLOG,
    isTextActive: assetType === TEXT,
  };
  return data;
}

export function* updateAddDeliverableFormOptions(action) {
  const deliverableId = action.deliverableId;
  const updateSubmitTypeCallback = action.callback;
  const selectedDeliverable = yield select(getDeliverableObject, deliverableId);

  if (selectedDeliverable) {
    const assetType = selectedDeliverable.get('assetType').toUpperCase();

    let data;
    if (assetType === VIDEO || assetType === IMAGE || assetType === TEXT || assetType === BLOG) {
      data = getAvailableSubmitTypes(assetType);
      data.selectedDeliverable = deliverableId;
      data.assetType = assetType;
    } else {
      data = {
        selectedDeliverable: deliverableId,
        assetType,
        submitType: 'url',
        isUploadActive: true,
        isUrlActive: true,
        isTextActive: true,
      };
    }
    updateSubmitTypeCallback(data.submitType);
    yield put({ type: SET_INITIAL_SUBMIT_TYPE, data });
  }
}

export function* watchSubmitDeliverable() {
  yield takeLatest(SUBMIT_DELIVERABLE, submitExistingDeliverable);
}

export function* watchUpdateAddDeliverableFormOptions() {
  yield takeLatest(UPDATE_ADD_DELIVERABLE_FORM_OPTIONS, updateAddDeliverableFormOptions);
}

export default function* deliverableSubmitSaga() {
  yield [
    fork(watchSubmitDeliverable),
    fork(watchUpdateAddDeliverableFormOptions),
  ];
}



/** WEBPACK FOOTER **
 ** ./src/containers/deliverable-submit-modal/sagas.js
 **/